/**
 * Token acquisition (roadmap D22, §3.5)
 *
 * Identity is created lazily. A visitor browses with no token and no record;
 * the first action that needs to persist something calls `ensureToken()`,
 * which creates a guest `users` record server-side and stores the token
 * returned. Everything afterwards uses it, and signing in later attaches
 * credentials to that same record.
 *
 * Reads never call this - only writes. A visitor who has merely looked
 * around should not leave a record behind.
 */

import { apiClient } from './apiClient';
import { localStorageService } from './localStorageService';

// The in-flight guest-creation request, if there is one.
//
// This is the single-flight guard, and it is required: two writes fired
// before the first token comes back would otherwise create *two* user
// records, the later token winning and the earlier write stranded on an
// orphan. Concurrent callers await this same promise instead of starting
// their own create.
//
// A global "block everything while one is in flight" lock would also fix it,
// but serializes unrelated reads and needs timeout handling so a hung
// request can't wedge the app. This is the smaller fix.
let tokenPromise = null;

export const getToken = () => localStorageService.getAuthToken();

export const setToken = (token) => {
  tokenPromise = null;
  localStorageService.setAuthToken(token);
};

export const clearToken = () => {
  tokenPromise = null;
  localStorageService.clearAuthToken();
};

/**
 * Resolve to a usable token, creating a guest account first if needed.
 * @returns {Promise<string>}
 */
export const ensureToken = () => {
  const existing = getToken();
  if (existing) return Promise.resolve(existing);

  if (!tokenPromise) {
    tokenPromise = apiClient.post('/users/guest', {})
      .then((response) => {
        // A sign-in may have landed a real token while this was in flight.
        // That one wins - overwriting it here would strand the account the
        // user just authenticated into.
        const current = localStorageService.getAuthToken();
        if (current) return current;

        localStorageService.setAuthToken(response.token);
        return response.token;
      })
      .catch((error) => {
        // Let the next write retry rather than caching the failure forever.
        tokenPromise = null;
        throw error;
      });
  }

  return tokenPromise;
};

/**
 * Reset the single-flight state. Tests only - the app has no reason to.
 */
export const resetTokenState = () => {
  tokenPromise = null;
};
